import React, { useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import AnalysisFilterActions from './AnalysisFilterActions';
import AleaDatePicker from '../../../components/AleaDatePicker/AleaDatePicker';

interface PeriodFilterProps {
  onCancel: () => void;
  onApply: (startDate: string, endDate: string) => void;
  initialStartDate?: string;
  initialEndDate?: string;
}

const PeriodFilter: React.FC<PeriodFilterProps> = ({
  onCancel,
  onApply,
  initialStartDate = '',
  initialEndDate = ''
}) => {
  // Parse the initial dates if they exist
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    const parts = dateStr.split('.');
    if (parts.length !== 3) return null;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    return !isNaN(date.getTime()) ? date : null;
  };
  
  const [startDate, setStartDate] = useState<Date | null>(parseDate(initialStartDate));
  const [endDate, setEndDate] = useState<Date | null>(parseDate(initialEndDate));

  // Handle start date change with validation
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    // If end date is before the new start date, clear end date
    if (date && endDate && endDate < date) {
      setEndDate(null);
    }
  };

  // Handle end date change with validation
  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Convert dates back to string format for the onApply function
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  };

  // Generate default dates if not set
  const getDefaultDates = () => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    return {
      startDate: formatDate(threeMonthsAgo),
      endDate: formatDate(today)
    };
  };

  const handleApply = () => {
    // Get default dates
    const defaultDates = getDefaultDates();
    
    // Format the dates and pass them to the parent component
    const formattedStartDate = formatDate(startDate) || defaultDates.startDate;
    const formattedEndDate = formatDate(endDate) || defaultDates.endDate;
    onApply(formattedStartDate, formattedEndDate);
  };

  return (
    <Flex flexFlow="column" justifyContent="space-between" alignItems="center">
      <Box borderRadius="10px" width="100%">
        <Flex alignItems="center" justifyContent="center">
          <Box width="190px">
            <AleaDatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              placeholderText="DD.MM.YYYY"
              onCancel={onCancel}
              maxDate={endDate || undefined}
            />
          </Box>
          
          <Text mx="15px" fontSize="20px" color="#2B4956">–</Text>
          
          <Box width="190px">
            <AleaDatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              placeholderText="DD.MM.YYYY"
              onCancel={onCancel}
              minDate={startDate || undefined}
            />
          </Box>
        </Flex>
      </Box>

      <AnalysisFilterActions onCancel={onCancel} onApply={handleApply} />
    </Flex>
  );
};

export default PeriodFilter;